import { pickT } from "@repo/shared";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ApiError } from "@/lib/api/_client";
import { detectBrowserLanguage } from "@/lib/i18n/detect-browser-language";
import { log } from "@/lib/logger/browser-logger";
import { T } from "./error-boundary.i18n";

function isChunkLoadError(error: Error): boolean {
  return (
    error.name === "ChunkLoadError" ||
    error.message?.includes("Loading chunk") ||
    error.message?.includes("Failed to fetch dynamically imported module") ||
    error.message?.includes("Importing a module script failed")
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isChunkLoadError(error)) {
      window.location.reload();
      return;
    }
    const requestId = error instanceof ApiError ? error.requestId : undefined;
    log.error({ err: error, componentStack: info.componentStack, requestId }, "application error");
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return <ErrorFallback onReset={this.handleReset} />;
    }
    return this.props.children;
  }
}

function ErrorFallback({ onReset }: { onReset: () => void }) {
  const t = pickT(T, detectBrowserLanguage());

  return (
    <div className="flex min-h-[50vh] items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6 text-center space-y-4">
          <h2 className="text-2xl font-bold">{t.somethingWentWrong}</h2>
          <p className="text-muted-foreground">{t.unexpectedError}</p>
          <div className="flex gap-2 justify-center">
            <Button onClick={onReset} variant="default">
              {t.tryAgain}
            </Button>
            <Button onClick={() => window.location.reload()} variant="outline">
              {t.refreshPage}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
